/**
 * Activity Panel Component
 *
 * Shows real-time persona activity on the right side of the chat.
 * Displays which personas are working, their status, and current tasks.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

export interface ActivePersona {
  personaId: string;
  name: string;
  icon: string;
  status: "spawning" | "working" | "complete" | "error";
  task?: string;
  label?: string;
  startedAt: number;
  completedAt?: number;
  error?: string;
  runId?: string;
  /** Estimated cost in cents for this task */
  costCents?: number;
}

export interface ActivityLogEntry {
  id: string;
  type: "step" | "tool" | "persona" | "cost";
  description: string;
  icon: string;
  status: "pending" | "running" | "complete" | "error";
  costCents?: number;
  timestamp: number;
  completedAt?: number;
  personaId?: string;
  toolName?: string;
}

const PERSONA_INFO: Record<string, { icon: string; name: string }> = {
  researcher: { icon: "🔍", name: "Researcher" },
  analyst: { icon: "📊", name: "Analyst" },
  bi: { icon: "🎯", name: "BI" },
  coder: { icon: "💻", name: "Coder" },
  security: { icon: "🔒", name: "Security" },
  pm: { icon: "📋", name: "PM" },
  legal: { icon: "⚖️", name: "Legal" },
  people: { icon: "👥", name: "People" },
  communicator: { icon: "✍️", name: "Communicator" },
  support: { icon: "🤝", name: "Support" },
  sales: { icon: "💰", name: "Sales" },
  finance: { icon: "📈", name: "Finance" },
};

@customElement("activity-panel")
export class ActivityPanel extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 280px;
      min-width: 200px;
      max-width: 400px;
      border-left: 1px solid var(--border-color, #333);
      background: var(--bg-secondary, #1a1a2e);
      overflow-y: auto;
      font-family: var(--font-sans, system-ui, sans-serif);
    }

    .panel-header {
      padding: 12px 16px;
      border-bottom: 1px solid var(--border-color, #333);
      font-weight: 600;
      font-size: 13px;
      color: var(--text-primary, #fff);
      display: flex;
      align-items: center;
      gap: 8px;
      background: var(--bg-primary, #0f0f1a);
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .panel-header .phase-badge {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 4px;
      background: var(--rax-yellow, #ffd000);
      color: #000;
      font-weight: 700;
      text-transform: uppercase;
    }

    .panel-content {
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .empty-state {
      padding: 24px 16px;
      text-align: center;
      color: var(--text-tertiary, #666);
      font-size: 12px;
    }

    .empty-state .icon {
      font-size: 32px;
      margin-bottom: 8px;
      opacity: 0.5;
    }

    .persona-card {
      padding: 12px;
      border-radius: 8px;
      background: var(--bg-primary, #0f0f1a);
      border: 1px solid var(--border-color, #333);
      position: relative;
      overflow: hidden;
    }

    .persona-card.working {
      border-left: 3px solid var(--rax-yellow, #ffd000);
      animation: pulse-border 2s ease-in-out infinite;
    }

    .persona-card.complete {
      border-left: 3px solid #22c55e;
      opacity: 0.7;
    }

    .persona-card.error {
      border-left: 3px solid #ef4444;
      background: rgba(239, 68, 68, 0.05);
    }

    @keyframes pulse-border {
      0%, 100% { border-left-color: var(--rax-yellow, #ffd000); }
      50% { border-left-color: rgba(255, 208, 0, 0.5); }
    }

    .persona-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }

    .persona-icon {
      font-size: 18px;
    }

    .persona-name {
      font-weight: 600;
      font-size: 13px;
      color: var(--text-primary, #fff);
      flex: 1;
    }

    .status-indicator {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .spinner {
      width: 12px;
      height: 12px;
      border: 2px solid var(--rax-yellow, #ffd000);
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .status-complete {
      color: #22c55e;
      font-size: 14px;
    }

    .status-error {
      color: #ef4444;
      font-size: 14px;
    }

    .persona-task {
      font-size: 11px;
      color: var(--text-secondary, #999);
      line-height: 1.4;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .persona-time {
      font-size: 10px;
      color: var(--text-tertiary, #666);
      margin-top: 6px;
    }

    .phase-section {
      margin-top: 16px;
    }

    .phase-label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-tertiary, #666);
      margin-bottom: 8px;
      padding-left: 4px;
    }

    .completed-section {
      opacity: 0.6;
    }

    .collapse-btn {
      background: none;
      border: none;
      color: var(--text-secondary, #999);
      cursor: pointer;
      padding: 4px;
      font-size: 12px;
    }

    .collapse-btn:hover {
      color: var(--text-primary, #fff);
    }

    /* Activity Log Styles */
    .activity-log {
      padding: 8px 0;
    }

    .log-entry {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 8px 12px;
      border-bottom: 1px solid var(--border-subtle, #222);
      font-size: 12px;
    }

    .log-entry:last-child {
      border-bottom: none;
    }

    .log-entry.running {
      background: rgba(255, 208, 0, 0.05);
    }

    .log-entry.complete {
      opacity: 0.7;
    }

    .log-entry.error {
      background: rgba(239, 68, 68, 0.05);
    }

    .log-icon {
      flex: 0 0 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      border-radius: 50%;
      background: var(--bg-elevated, #222);
    }

    .log-icon.running {
      background: var(--rax-yellow, #ffd000);
      color: #000;
      animation: pulse 1.5s infinite;
    }

    .log-icon.complete {
      background: #22c55e;
      color: #fff;
    }

    .log-icon.error {
      background: #ef4444;
      color: #fff;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(0.9); }
    }

    .log-content {
      flex: 1;
      min-width: 0;
    }

    .log-description {
      color: var(--text-primary, #fff);
      line-height: 1.3;
    }

    .log-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 2px;
      font-size: 10px;
      color: var(--text-tertiary, #666);
    }

    .log-cost {
      padding: 1px 6px;
      background: rgba(255, 208, 0, 0.15);
      color: var(--rax-yellow, #ffd000);
      border-radius: 4px;
      font-weight: 600;
    }

    .log-time {
      color: var(--text-tertiary, #666);
    }

    .total-cost {
      padding: 12px;
      margin: 8px 12px;
      background: linear-gradient(135deg, rgba(255, 208, 0, 0.1), rgba(255, 161, 0, 0.05));
      border: 1px solid rgba(255, 208, 0, 0.2);
      border-radius: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .total-label {
      font-size: 12px;
      color: var(--text-secondary, #999);
      font-weight: 600;
    }

    .total-amount {
      font-size: 16px;
      font-weight: 700;
      color: var(--rax-yellow, #ffd000);
    }

    .section-header {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-tertiary, #666);
      padding: 8px 12px 4px;
      font-weight: 600;
    }
  `;

  @state()
  private personas: Map<string, ActivePersona> = new Map();

  @state()
  private activityLog: ActivityLogEntry[] = [];

  @state()
  private collapsed = false;

  @state()
  private currentPhase: "idle" | "phase1" | "phase2" | "synthesizing" = "idle";

  private _boundSpawnHandler?: (e: Event) => void;
  private _boundCompletedHandler?: (e: Event) => void;

  connectedCallback() {
    super.connectedCallback();
    this._boundSpawnHandler = (e: Event) => this.handlePersonaSpawned(e as CustomEvent);
    this._boundCompletedHandler = (e: Event) => this.handlePersonaCompleted(e as CustomEvent);
    window.addEventListener("persona-spawned", this._boundSpawnHandler);
    window.addEventListener("persona-completed", this._boundCompletedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._boundSpawnHandler) {
      window.removeEventListener("persona-spawned", this._boundSpawnHandler);
    }
    if (this._boundCompletedHandler) {
      window.removeEventListener("persona-completed", this._boundCompletedHandler);
    }
  }

  private handlePersonaSpawned(e: CustomEvent<{
    personaId: string;
    runId?: string;
    task?: string;
    label?: string;
  }>) {
    const { personaId, runId, task, label } = e.detail;
    const info = PERSONA_INFO[personaId] || { icon: "👤", name: personaId };

    const persona: ActivePersona = {
      personaId,
      name: info.name,
      icon: info.icon,
      status: "working",
      task,
      label,
      startedAt: Date.now(),
      runId,
    };

    this.personas = new Map(this.personas.set(personaId, persona));

    // Add to activity log
    const logEntry: ActivityLogEntry = {
      id: `persona-${personaId}-${Date.now()}`,
      type: "persona",
      description: label || task || `${info.name} working...`,
      icon: info.icon,
      status: "running",
      timestamp: Date.now(),
      personaId,
    };
    this.activityLog = [...this.activityLog, logEntry];

    // Determine phase based on personas active
    this.updatePhase();
  }

  private handlePersonaCompleted(e: CustomEvent<{
    personaId: string;
    success: boolean;
    task?: string;
    label?: string;
    costCents?: number;
  }>) {
    const { personaId, success, costCents } = e.detail;
    const existing = this.personas.get(personaId);

    if (existing) {
      existing.status = success ? "complete" : "error";
      existing.completedAt = Date.now();
      existing.costCents = costCents;
      this.personas = new Map(this.personas);
    }

    // Update activity log entry
    this.activityLog = this.activityLog.map(entry => {
      if (entry.personaId === personaId && entry.status === "running") {
        return {
          ...entry,
          status: success ? "complete" : "error" as const,
          completedAt: Date.now(),
          costCents,
        };
      }
      return entry;
    });

    // Check if all done
    this.updatePhase();
  }

  /**
   * Add a step to the activity log
   */
  public addStep(description: string, icon = "📋"): string {
    const id = `step-${Date.now()}`;
    const entry: ActivityLogEntry = {
      id,
      type: "step",
      description,
      icon,
      status: "pending",
      timestamp: Date.now(),
    };
    this.activityLog = [...this.activityLog, entry];
    return id;
  }

  /**
   * Start a step (mark as running)
   */
  public startStep(id: string): void {
    this.activityLog = this.activityLog.map(entry =>
      entry.id === id ? { ...entry, status: "running" as const, timestamp: Date.now() } : entry
    );
  }

  /**
   * Complete a step with optional cost
   */
  public completeStep(id: string, costCents?: number): void {
    this.activityLog = this.activityLog.map(entry =>
      entry.id === id
        ? { ...entry, status: "complete" as const, completedAt: Date.now(), costCents }
        : entry
    );
  }

  /**
   * Mark a step as error
   */
  public errorStep(id: string): void {
    this.activityLog = this.activityLog.map(entry =>
      entry.id === id ? { ...entry, status: "error" as const, completedAt: Date.now() } : entry
    );
  }

  /**
   * Add a tool use entry
   */
  public addToolUse(toolName: string, description: string, icon = "⚙️"): string {
    const id = `tool-${toolName}-${Date.now()}`;
    const entry: ActivityLogEntry = {
      id,
      type: "tool",
      description,
      icon,
      status: "running",
      timestamp: Date.now(),
      toolName,
    };
    this.activityLog = [...this.activityLog, entry];
    return id;
  }

  /**
   * Complete a tool use
   */
  public completeToolUse(id: string, costCents?: number): void {
    this.completeStep(id, costCents);
  }

  /**
   * Get total cost from activity log
   */
  public getTotalCost(): number {
    return this.activityLog.reduce((sum, entry) => sum + (entry.costCents || 0), 0);
  }

  /**
   * Clear the activity log
   */
  public clearLog(): void {
    this.activityLog = [];
    this.personas = new Map();
  }

  private formatCost(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  private updatePhase() {
    const working = [...this.personas.values()].filter(p => p.status === "working");
    const researchTeam = ["researcher", "analyst", "bi", "sales"];
    const industryExperts = ["coder", "security", "support", "legal", "finance", "pm", "people", "communicator"];

    const hasResearchWorking = working.some(p => researchTeam.includes(p.personaId));
    const hasExpertsWorking = working.some(p => industryExperts.includes(p.personaId));

    if (working.length === 0) {
      if (this.personas.size > 0) {
        this.currentPhase = "synthesizing";
        // Clear after showing synthesis
        setTimeout(() => {
          this.personas = new Map();
          this.currentPhase = "idle";
        }, 3000);
      } else {
        this.currentPhase = "idle";
      }
    } else if (hasResearchWorking && !hasExpertsWorking) {
      this.currentPhase = "phase1";
    } else if (hasExpertsWorking) {
      this.currentPhase = "phase2";
    } else {
      this.currentPhase = "phase1";
    }
  }

  private formatDuration(startedAt: number, completedAt?: number): string {
    const end = completedAt || Date.now();
    const seconds = Math.floor((end - startedAt) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  }

  private renderPersonaCard(persona: ActivePersona) {
    const taskDisplay = persona.label || persona.task || "Working...";
    const truncatedTask = taskDisplay.length > 80 ? `${taskDisplay.slice(0, 77)}...` : taskDisplay;

    return html`
      <div class="persona-card ${persona.status}">
        <div class="persona-header">
          <span class="persona-icon">${persona.icon}</span>
          <span class="persona-name">${persona.name}</span>
          <span class="status-indicator">
            ${persona.status === "working"
              ? html`<span class="spinner"></span>`
              : persona.status === "complete"
                ? html`<span class="status-complete">✓</span>`
                : html`<span class="status-error">⚠</span>`}
          </span>
        </div>
        <div class="persona-task">${truncatedTask}</div>
        <div class="persona-time">${this.formatDuration(persona.startedAt, persona.completedAt)}</div>
      </div>
    `;
  }

  private getPhaseLabel(): string {
    switch (this.currentPhase) {
      case "phase1": return "Phase 1: Research Team";
      case "phase2": return "Phase 2: Industry Experts";
      case "synthesizing": return "Synthesizing...";
      default: return "Team Activity";
    }
  }

  private getPhaseBadge(): string {
    switch (this.currentPhase) {
      case "phase1": return "P1";
      case "phase2": return "P2";
      case "synthesizing": return "⚡";
      default: return "";
    }
  }

  private renderLogEntry(entry: ActivityLogEntry) {
    return html`
      <div class="log-entry ${entry.status}">
        <span class="log-icon ${entry.status}">
          ${entry.status === "running" ? "⋯" :
            entry.status === "complete" ? "✓" :
            entry.status === "error" ? "✗" : entry.icon}
        </span>
        <div class="log-content">
          <div class="log-description">${entry.description}</div>
          <div class="log-meta">
            ${entry.costCents ? html`<span class="log-cost">${this.formatCost(entry.costCents)}</span>` : ""}
            <span class="log-time">${this.formatDuration(entry.timestamp, entry.completedAt)}</span>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    const working = [...this.personas.values()].filter(p => p.status === "working");
    const completed = [...this.personas.values()].filter(p => p.status !== "working");
    const totalCost = this.getTotalCost();
    const hasActivity = this.activityLog.length > 0 || this.personas.size > 0;

    if (!hasActivity && this.currentPhase === "idle") {
      return html`
        <div class="panel-header">
          <span>Team Activity</span>
        </div>
        <div class="empty-state">
          <div class="icon">👥</div>
          <div>No active tasks</div>
          <div style="margin-top: 4px; font-size: 11px;">
            Activity will appear here when working
          </div>
        </div>
      `;
    }

    return html`
      <div class="panel-header">
        <span>${this.getPhaseLabel()}</span>
        ${this.getPhaseBadge() ? html`<span class="phase-badge">${this.getPhaseBadge()}</span>` : ""}
      </div>

      <!-- Total Cost Summary -->
      ${totalCost > 0 ? html`
        <div class="total-cost">
          <span class="total-label">Total Cost</span>
          <span class="total-amount">${this.formatCost(totalCost)}</span>
        </div>
      ` : ""}

      <div class="panel-content">
        <!-- Activity Log -->
        ${this.activityLog.length > 0 ? html`
          <div class="section-header">Activity Log</div>
          <div class="activity-log">
            ${this.activityLog.map(entry => this.renderLogEntry(entry))}
          </div>
        ` : ""}

        <!-- Persona Cards -->
        ${working.length > 0 ? html`
          <div class="phase-section">
            <div class="phase-label">Working (${working.length})</div>
            ${working.map(p => this.renderPersonaCard(p))}
          </div>
        ` : ""}

        ${completed.length > 0 ? html`
          <div class="phase-section completed-section">
            <div class="phase-label">Completed (${completed.length})</div>
            ${completed.map(p => this.renderPersonaCard(p))}
          </div>
        ` : ""}

        ${this.currentPhase === "synthesizing" ? html`
          <div class="persona-card working">
            <div class="persona-header">
              <span class="persona-icon">🎯</span>
              <span class="persona-name">Ajax (Orchestrator)</span>
              <span class="status-indicator">
                <span class="spinner"></span>
              </span>
            </div>
            <div class="persona-task">Synthesizing team findings...</div>
          </div>
        ` : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "activity-panel": ActivityPanel;
  }
}
